import DOMPurify from 'dompurify'
import { http } from '../services/http'
import { COMPONENT_CONFIG, HighlightAsset, VideoData } from './expoStand'

export const sanitizeHtml = (htmlStr: string): string => DOMPurify.sanitize(htmlStr)

export const sanitizeHtmlWithLinks = (htmlStr: string): string => {
  const cleanHtml = DOMPurify.sanitize(htmlStr, {
    ADD_ATTR: ['target', 'rel'],
  })
  const parsed = new DOMParser().parseFromString(cleanHtml, 'text/html')

  parsed.querySelectorAll('a').forEach((anchor) => {
    const href = anchor.getAttribute('href') || ''
    if (href && !/^(https?:|mailto:|tel:)/i.test(href)) {
      anchor.removeAttribute('href')
    }

    if (anchor.getAttribute('target') === '_blank') {
      const relValues = (anchor.getAttribute('rel') || '').split(/\s+/).filter(Boolean)
      if (!relValues.includes('noopener')) relValues.push('noopener')
      if (!relValues.includes('noreferrer')) relValues.push('noreferrer')
      anchor.setAttribute('rel', relValues.join(' '))
    }
  })

  return parsed.body.innerHTML
}

export const generateEmbedUrl = (url?: string): null | string => {
  if (!url) return null
  const vimeoReg = /^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/
  const youtubeReg = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  if (vimeoReg.test(url)) {
    const match = url.match(vimeoReg)
    if (match && match?.[5]) return `https://player.vimeo.com/video/${match?.[5]}`
  }
  if (youtubeReg.test(url)) {
    const match = url.match(youtubeReg)
    if (match && match?.[2].length === 11) return `https://www.youtube.com/embed/${match[2]}`
  }
  return null
}

const isVimeoUrl = (url: string): boolean => /(?:^|\/\/)(?:www\.|player\.)?vimeo\.com\//i.test(url)

export const getVideoData = (url: string): Promise<VideoData | null> => {
  const oembedUrl = isVimeoUrl(url)
    ? `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`
    : `https://youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`

  return http.os
    .get({
      url: oembedUrl,
      transformRequest: (data, headers) => {
        delete headers.Authorization
        return data
      },
    })
    .then((res) => {
      const embedUrl = generateEmbedUrl(url)
      const title = res.data?.title
      const thumbnailUrl = res.data?.thumbnail_url
      if (!title || !thumbnailUrl || !embedUrl) return null
      return { title, thumbnail_url: thumbnailUrl, video_url: embedUrl }
    })
    .catch((err) => {
      console.error('Failed to retrieve video data: ', err)
      return null
    })
}

export const getFile = (file?: string | any[] | null) =>
  file ? (typeof file === 'string' ? file : file?.[0]?.src) : undefined

export const getHighlightBackgroundUrl = (
  highlights: Record<string, HighlightAsset> | undefined,
  highlightId: string
): string | undefined => {
  const highlightAsset = highlights?.[highlightId]
  if (!highlightAsset) return undefined

  const backgroundImage = highlightAsset.backgroundImage
  return backgroundImage ? getFile(backgroundImage) : undefined
}

const EXPO_NAVBAR_EXCLUDE = ['navbar', 'cover', 'chat']
export const parseNavLinks = (contents) =>
  contents
    ?.filter((block) => !EXPO_NAVBAR_EXCLUDE.includes(block.contentType))
    .map((block) => {
      const displayData = COMPONENT_CONFIG[block.contentType]
      return {
        label: block.data?.navHeading || displayData?.navLabel || displayData?.label,
        onClick: () => {
          window.location.hash = '#' + block.contentType
        },
      }
    })

const getReorderableBlocks = (reorderable = true) =>
  Object.keys(COMPONENT_CONFIG).filter((x) => {
    const value = COMPONENT_CONFIG[x]
    return value.reorderable === reorderable
  })

export const bottomFixedBlocks = ['chat']
export const reorderableBlocks = getReorderableBlocks()
export const nonReorderableBlocks = getReorderableBlocks(false).filter((x) => !bottomFixedBlocks.includes(x))

const fullMonthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export const formatDate = (dateStr: string) => {
  if (!dateStr) return undefined
  const dateObj = new Date(dateStr)
  return `${fullMonthNames[dateObj.getMonth()]} ${dateObj.getDate()}, ${dateObj.getFullYear()}`
}

// This will pre-load the image to check if the image url is a working/valid image url
export const isValidImageUrl = (url: string): Promise<boolean> =>
  new Promise((resolve) => {
    if (!url) return resolve(false)
    const img = new Image()
    img.src = url
    img.onload = () => resolve(true)
    img.onerror = () => resolve(false)
  })

export class FormatterUtils {
  static formatPercentageValue = (v: number | string | null, places: number = 2): string => {
    if (v === null || !v) return ''
    const numValue = typeof v === 'string' ? parseFloat(v) : v

    const multiplier = 10 ** places

    // Work around for floating point number precision issue
    const formattedValue = Math.round(numValue * 100 * multiplier) / multiplier

    return String(formattedValue)
  }

  // To complete list use this ref and replace convert to iso2:
  // https://gist.github.com/voskobovich/43f851859c23a8261514
  static currencySymbolForCountry = (countryCode) => {
    switch (countryCode) {
      case 'AF': // Afghanistan
        return '؋'
      case 'VN': // Vietnam
        return '₫'
      case 'DZ': // Algeria
        return 'DA'
      case 'TH': // Thailand
        return '฿'
      case 'NG': // Nigeria
        return '₦'
      case 'AW': // Aruba
      case 'AN': // Netherlands Antilles
        return 'ƒ'
      case 'BA': // Bosnia and Herzegovina
        return 'KM'
      case 'BR': // Brazil
        return 'R$'
      case 'BW': // Botswana
        return 'P'
      case 'BG': // Bulgaria
        return '€'
      case 'MM': // Myanmar/Burma
        return 'K'
      case 'CN': // China
        return 'E£'
      case 'JP': // Japan
        return '¥'
      case 'CR': // Costa Rica
        return '₡'
      case 'HR': // Croatia
        return '€'
      case 'CZ': // Czech Republic
        return 'Kč'
      case 'CD': // DR Congo
        return 'FC'
      case 'ID': // Indonesia
        return 'Rp'
      case 'IN': // India
        return '₹'
      case 'PK': // Pakistan
      case 'NP': // Nepal
      case 'LK': // Sri Lanka
      case 'SC': // Seychelles
      case 'MU': // Mauritius
        return '₨'
      case 'PH': // Philippines
        return '₱'
      case 'GH': // Ghana
        return '₵'
      case 'GT': // Guatemala
        return 'Q'
      case 'HN': // Honduras
        return 'L'
      case 'HU': // Hungary
        return 'Ft '
      case 'IR': // Iran
        return '﷼'
      case 'IQ': // Iraq
        return 'د.ع'
      case 'IL': // Israel
        return '₪'
      case 'JO': // Jordan
        return 'JD'
      case 'KZ': // Kazakhstan
        return '₸'
      case 'KE': // Kenya
        return 'KSh'
      case 'KW': // Kuwait
        return 'KD'
      case 'LV': // Latvia
        return 'Ls'
      case 'MY': // Malaysia
        return 'RM'
      case 'MA': // Morocco
        return 'DH'
      case 'PA': // Panama
        return 'B/.'
      case 'PE': // Peru
        return 'PEN'
      case 'PL': // Poland
        return 'zł'
      case 'QA': // Qatar
        return 'QR'
      case 'RO': // Romania
        return 'lei'
      case 'RU': // Russia
        return '₽'
      case 'SA': // Saudi Arabia
        return 'SR'
      case 'RS': // Serbia
        return 'дин'
      // case 'CI': // Ivory Coast
      //   return 'CFA'
      case 'VU': // Vanuatu
        return 'VT'
      case 'CM': // Cameroon
        return 'FCFA'
      case 'ZA': // South Africa
        return 'R'
      case 'KR': // South Korea
        return '₩'
      case 'CH': // Switzerland
        return 'CHF'
      case 'CL': // Chile
        return 'CLP$'
      case 'TZ': // Tanzania
        return 'TSh'
      case 'TN': // Tunisia
        return 'DT'
      case 'TR': // Turkey
        return '₺'
      case 'AE': // UAE
        return 'DH'
      case 'UG': // Uganda
        return 'USh'
      case 'UA': // Ukraine
        return '₴'
      case 'ZM': // Zambia
        return 'K'
      case 'ZW': // Zimbabwe
        return 'Z$'
      case 'EG': // Egypt
        return 'E£'
      case 'UZ': // Uzbekistan
        return 'UZS'
      case 'NA': // Namibia
        return 'N$'
      case 'NC': // New Caledonia
        return 'XPF'
      case 'AO': // Angola
        return 'AOA'
      case 'ML': // Mali
      case 'NE': // Niger
      case 'SN': // Senegal
      case 'TG': // Togo
      case 'GW': // Guinea-Bissau
      case 'CI': // Cote d'Ivorie
      case 'BJ': // Benin
      case 'BF': // Burkina Faso
        return 'XOF'
      case 'GB': // United Kingdom
      case 'FK': // Falkland Island
      case 'GI': // Gibraltar
      case 'LB': // Lebanon
      case 'SH': // Saint Helena
      case 'SY': // Syria
      case 'IM': // Isle of Man
      case 'GG': // Guernsey
      case 'JE': // Jersey
        return '£'
      case 'SE': // Sweden
        return 'SEK '
      case 'DK': // Denmark
      case 'NO': // Norway
      case 'IS': // Iceland
        return 'kr'
      case 'AZ': // Azerbaijan
        return '₼'
      case 'BH': // Bahrain
        return 'BD'
      case 'BD': // Bangladesh
        return 'Tk'
      case 'BB': // Barbados
        return '$'
      case 'BY': // Belarus
        return 'p'
      case 'BO': // BOLIVIA
        return '$b'
      case 'MG': // Madagascar
        return 'Ar'
      case 'MR': // Mauritania
        return 'MRU'
      case 'PG': // Papua New Guinea
        return 'K'
      case 'AT': // Austria
      case 'BE': // Belgium
      case 'CY': // Cyprus
      case 'EE': // Estonia
      case 'FI': // Finland
      case 'FR': // France
      case 'DE': // Germany
      case 'GR': // Greece
      case 'IE': // Ireland
      case 'IT': // Italy
      case 'LT': // Lithuania
      case 'LU': // Luxembourg
      case 'MT': // Malta
      case 'NL': // Netherlands
      case 'PT': // Portugal
      case 'SK': // Slovakia
      case 'SI': // Slovenia
      case 'ES': // Spain
        return '€'
      default:
        return '$'
    }
  }

  static formatCurrencyNumber = (value: number, places: number, locale?: string) => {
    if (!places && places !== 0) {
      places = 2
    }
    locale = this.convertLocaleName(locale)

    return new Intl.NumberFormat(locale, { minimumFractionDigits: places, maximumFractionDigits: places }).format(
      value ? value : 0
    )
  }

  static convertLocaleName = (locale?: string): string | undefined => {
    if (!locale) {
      return locale
    } else if (!locale.includes('_')) {
      return locale
    } else {
      const parts = locale.split('_')
      return `${parts[0]}-${parts[1]}`
    }
  }
}

export const getReferrer = () => {
  try {
    return document.referrer ? new URL(document.referrer).hostname : null
  } catch (error) {
    console.error('Failed to get referrer: ', error)
    return null
  }
}
